import { OpenAPI } from '@beeboard/common';
import axios from 'axios';
import {
  deleteCachedEntry,
  getCachedEntry,
  isCacheFresh,
  setCachedEntry,
} from '@/utils/helpers/blobCache';

export async function resolveOpenAPIToken(): Promise<string> {
  return typeof OpenAPI.TOKEN === 'function'
    ? await OpenAPI.TOKEN({} as never)
    : (OpenAPI.TOKEN ?? '');
}

async function fetchBlobUrl(path: string, noCache = false): Promise<string> {
  const token = await resolveOpenAPIToken();
  const cached = noCache ? undefined : await getCachedEntry(path);

  if (cached && isCacheFresh(cached)) {
    return URL.createObjectURL(cached.blob);
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(noCache && { 'Cache-Control': 'no-cache' }),
  };
  if (cached?.etag) {
    headers['If-None-Match'] = cached.etag;
  }

  try {
    const response = await axios.get(`${OpenAPI.BASE}${path}`, {
      responseType: 'blob',
      headers,
      validateStatus: (status) => status === 200 || status === 304,
    });

    if (response.status === 304 && cached) {
      return URL.createObjectURL(cached.blob);
    }

    const etag = (response.headers['etag'] as string) ?? null;
    void setCachedEntry(path, response.data, etag);

    return URL.createObjectURL(response.data);
  } catch (err) {
    if (cached) {
      return URL.createObjectURL(cached.blob);
    }
    throw err;
  }
}

export async function invalidateBlobCache(path: string): Promise<void> {
  await deleteCachedEntry(path);
}

export function fetchProfilePictureBlobUrl(
  userId: string,
  noCache = false,
): Promise<string> {
  return fetchBlobUrl(`/v1/users/${userId}/profile-picture`, noCache);
}

export function fetchProjectImageBlobUrl(
  projectId: string,
  noCache = false,
): Promise<string> {
  return fetchBlobUrl(`/v1/projects/${projectId}/image`, noCache);
}
